import { useState } from 'react';

import PersonIcon from '@mui/icons-material/Person';
import { Box } from '@mui/material';

import { cx } from '@/styles/muiTheme';

type Props = {
  name: string;
  headshotUrl: string | null;
  size?: number;
};

/**
 * Circular roster portrait with a person-icon fallback.
 *
 * Headshots are wide and framed with headroom (the NBA CDN serves 1040x760), so
 * a centred square crop clips the chin — `objectPosition: top` keeps the face in
 * the circle.
 *
 * The URL is whatever the roster import stored and points at a league CDN we do
 * not control, so a 404 is a normal outcome. Tracking the failed *url* rather
 * than a boolean matters here: the trigger keeps one instance across selections,
 * and a boolean would leave it stuck on the fallback for every later player.
 */
const PlayerAvatar = ({ name, headshotUrl, size = 24 }: Props) => {
  const [failedUrl, setFailedUrl] = useState<string | null>(null);

  const shared = {
    width: size,
    height: size,
    borderRadius: '50%',
    flexShrink: 0,
    backgroundColor: cx.bg.elevated,
  } as const;

  if (headshotUrl && headshotUrl !== failedUrl) {
    return (
      <Box
        component="img"
        src={headshotUrl}
        alt={name}
        onError={() => setFailedUrl(headshotUrl)}
        sx={{ ...shared, objectFit: 'cover', objectPosition: 'top' }}
      />
    );
  }

  return (
    <Box sx={{ ...shared, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <PersonIcon sx={{ fontSize: Math.round(size * 0.6), color: cx.text.tertiary }} />
    </Box>
  );
};

export default PlayerAvatar;
